import { eventModal } from "@NextSkyTheme/modal";
import { notifier } from "@NextSkyTheme/notification";

/* Tiroir « Aide à la mesure » (sections/kt-measure.liquid).
   Quatre questions (la fenêtre, sa position, la tringle, les rideaux), puis la taille à commander ; le schéma du mur,
   de la fenêtre, de la tringle et des rideaux se redessine à chaque saisie et se recadre sur ce que mesure l'étape.
   « Appliquer cette taille » envoie kt:measure:apply { unit, width, height, widthCm, heightCm, qty, rodCm, mount } : le bloc des
   tailles (snippets/sur-mesure-asset.liquid) l'écrit dans les champs sur mesure et la quantité.
   L'unité est celle du bloc des tailles (#KtUnitSelect) : changer l'une change l'autre. */

const IN = 2.54;
const RANGES = {
  winW: [20, 2000],
  winH: [20, 600],
  sill: [0, 400],
  above: [0, 300],
  overhang: [0, 150],
  ceil: [0, 400],
};
const FRAC = ["", "⅛", "¼", "⅜", "½", "⅝", "¾", "⅞"];
const FRAC_TXT = { "⅛": "1/8", "¼": "1/4", "⅜": "3/8", "½": "1/2", "⅝": "5/8", "¾": "3/4", "⅞": "7/8" };
const kOf = (u) => (u === "in" ? IN : 1);
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
const headerOffset = () =>
  (parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0) + 16;

// Calcul, fonction pure. inp : valeurs en cm (null = vide, NaN = illisible) ; S : réglages par unité.
// Renvoie les cotes en cm, et la taille à commander dans l'unité choisie, arrondie au cm ou au 1/8 po supérieur.
export function measureCalc(inp, S) {
  const u = inp.unit,
    Dd = S[u],
    k = kOf(u),
    err = {};
  function need(key, req) {
    const v = inp[key],
      r = RANGES[key];
    if (v === null || v === undefined) {
      if (req) err[key] = "empty";
      return;
    }
    if (Number.isNaN(v)) {
      err[key] = "nan";
      return;
    }
    if (v < r[0] - 1e-9 || v > r[1] + 1e-9) err[key] = { min: r[0], max: r[1] };
  }
  need("winW", true);
  need("winH", true);
  need("sill", true);
  need("overhang", true);
  if (inp.mount === "wall") {
    need("above", true);
    need("ceil", false);
  } else need("ceil", true);
  if (
    inp.mount === "wall" &&
    !err.above &&
    !err.ceil &&
    inp.ceil !== null &&
    inp.ceil !== undefined &&
    inp.above > inp.ceil
  )
    err.above = "ceiling";
  if (inp.mount === "ceiling" && !err.ceil && inp.ceil <= Dd.ceilClear * k) err.ceil = "clear";
  const res = { ok: Object.keys(err).length === 0, err: err, unit: u };
  if (!res.ok) return res;
  const fullness = inp.full === "opt" ? Dd.fullOpt : Dd.fullReco;
  const brk = inp.floor === "break" ? Dd.brk * k : 0;
  const top = inp.sill + inp.winH;
  const ceilY = inp.ceil === null || inp.ceil === undefined ? null : top + inp.ceil;
  const rodLen = inp.winW + 2 * inp.overhang;
  const rodY = inp.mount === "wall" ? top + inp.above : ceilY;
  const curtTop = inp.mount === "wall" ? rodY : ceilY - Dd.ceilClear * k;
  const baseLen = Dd.base === "rod" ? rodLen : inp.winW;
  const totalW = baseLen * fullness,
    panelW = totalW / inp.panels,
    drop = curtTop + brk;
  let orderW, orderH;
  if (u === "in") {
    orderW = Math.ceil((panelW / IN) * 8 - 1e-6) / 8;
    orderH = Math.ceil((drop / IN) * 8 - 1e-6) / 8;
  } else {
    orderW = Math.ceil(panelW - 1e-6);
    orderH = Math.ceil(drop - 1e-6);
  }
  return Object.assign(res, {
    fullness: fullness,
    base: Dd.base,
    brk: brk,
    top: top,
    ceilY: ceilY,
    rodLen: rodLen,
    rodY: rodY,
    curtTop: curtTop,
    baseLen: baseLen,
    totalW: totalW,
    panelW: panelW,
    drop: drop,
    qty: inp.panels,
    orderW: orderW,
    orderH: orderH,
    orderWcm: orderW * k,
    orderHcm: orderH * k,
  });
}

// "47 1/2", "47½", "47,5", "120 cm" -> valeur en cm (null = vide, NaN = illisible).
function parseLen(txt, u) {
  let s = String(txt || "").trim();
  if (!s) return null;
  s = s
    .replace(/[⅛¼⅜½⅝¾⅞]/g, (m) => " " + FRAC_TXT[m])
    .replace(/,/g, ".")
    .replace(/\s*(cm|po|in|"|″)\s*$/i, "")
    .trim();
  const m = s.match(/^(\d+(?:\.\d+)?)?(?:[\s-]*(\d+)\/(\d+))?$/);
  if (!m || (m[1] === undefined && m[2] === undefined)) return NaN;
  let v = m[1] ? parseFloat(m[1]) : 0;
  if (m[2]) {
    const d = +m[3];
    if (!d) return NaN;
    v += +m[2] / d;
  }
  return v * kOf(u);
}

function init(ms) {
  const D = document;
  const cfg = JSON.parse(ms.querySelector("[data-kt-ms-config]").textContent);
  // Le filtre de traduction échappe le HTML : on décode une fois.
  const decode = (s) => {
    const ta = D.createElement("textarea");
    ta.innerHTML = s;
    return ta.value;
  };
  const T = {};
  Object.keys(cfg.t).forEach((key) => (T[key] = decode(cfg.t[key] || "")));
  const tr = (key, vars) =>
    String(T[key] || "").replace(/\{(\w+)\}/g, (m, name) => (vars && vars[name] !== undefined ? vars[name] : m));
  const S = cfg.settings;
  const PERF = cfg.performance || "insulating";
  // Couleur du rideau dans le schéma : une couleur CSS simple (#hex, rgb[a]()), sinon celle par défaut. Elle vient
  // d'un metafield et part dans un attribut du SVG.
  const safeColor = (c) => {
    const v = String(c || "").trim();
    return /^(#[0-9a-f]{3,8}|rgba?\([\d.,\s%]+\))$/i.test(v) ? v : null;
  };
  const CURTAIN = safeColor(cfg.curtain) || "#d8d2c8";
  const LOCALE = cfg.locale || D.documentElement.lang || "fr";
  const UNIT_IN = decode(cfg.unitIn || "in");
  const ANCHOR = ms.dataset.anchor;
  const $ = (s, r) => (r || ms).querySelector(s);
  const $$ = (s, r) => Array.prototype.slice.call((r || ms).querySelectorAll(s));

  /* ================= Formats ================= */
  function fmtU(x, u, dec) {
    if (u === "in") {
      const e = Math.round(x * 8),
        w = Math.floor(e / 8),
        r = e - w * 8;
      return r ? (w ? w + " " + FRAC[r] : FRAC[r]) : String(w);
    }
    const p = Math.pow(10, dec === undefined ? 1 : dec);
    return (Math.round(x * p) / p).toLocaleString(LOCALE, {
      maximumFractionDigits: dec === undefined ? 1 : dec,
      useGrouping: false,
    });
  }
  const fmtNum = (x) => (Math.round(x * 100) / 100).toLocaleString(LOCALE, { maximumFractionDigits: 2 });
  // Unité : « cm » après une espace ; le symbole des pouces (″) collé, comme dans le bloc des tailles.
  const UL = () => (st.unit === "in" ? UNIT_IN : "cm");
  const withUnit = (s) => (/^[a-z]/i.test(UL()) ? s + " " + UL() : s + UL());
  const L = (cm, dec) => fmtU(cm / kOf(st.unit), st.unit, dec);
  const U = (cm, dec) => withUnit(L(cm, dec));
  const good = (x) => typeof x === "number" && !Number.isNaN(x);

  /* ================= État ================= */
  const FIELDS = ["winW", "winH", "sill", "above", "overhang", "ceil"];
  const STEP_KEYS = [["winW", "winH"], ["sill"], ["above", "overhang", "ceil"], [], []];
  // Cotes mises en avant à chaque étape ; les autres sont estompées.
  const REL = [
    ["winW", "winH"],
    ["sill"],
    ["above", "overhang", "rod", "ceil"],
    ["drop", "panel", "rod"],
    ["rod", "drop", "panel", "overhang", "ceil", "winW", "winH", "sill", "above"],
  ];
  // Fenêtre dessinée tant que ses cotes manquent (marquées « ? »).
  const PH = { winW: 120, winH: 135, sill: 90 };
  const pdpContainer = () => D.querySelector(".sur-mesure-all-container:not(.kt-ms__body)");
  const pdpUnit = () => {
    const c = pdpContainer();
    return c && c.dataset.ktUnit ? c.dataset.ktUnit : null;
  };
  const st = {
    unit: pdpUnit() || cfg.unit || "cm",
    v: { winW: null, winH: null, sill: null, above: null, overhang: null, ceil: null },
    pristine: { above: true, overhang: true },
    mount: "wall",
    panels: 2,
    side: "left",
    full: "reco",
    floor: "touch",
    view: "auto",
    step: 0,
    focus: null,
    shown: {},
    // Coloris choisi à l'étape 5 : celui de la fiche au départ ; url = la fiche d'un autre coloris.
    color: { name: (cfg.color || {}).name || "", style: (cfg.color || {}).style || "", url: null },
    curtain: CURTAIN,
    // Fixation choisie à l'étape 5 (valeur de l'option de la fiche) ; quantité (null = le nombre de rideaux).
    fixation: null,
    qty: null,
  };
  if (st.unit !== "in") st.unit = "cm";
  function inputs() {
    return {
      unit: st.unit,
      winW: st.v.winW,
      winH: st.v.winH,
      sill: st.v.sill,
      above: st.v.above,
      overhang: st.v.overhang,
      ceil: st.v.ceil,
      mount: st.mount,
      panels: st.panels,
      full: st.full,
      floor: st.floor,
    };
  }
  const calc = () => measureCalc(inputs(), S);
  // Débord et hauteur au-dessus de la fenêtre : la valeur conseillée, tant que le client ne l'a pas changée.
  function applyDefaults() {
    const Dd = S[st.unit],
      k = kOf(st.unit);
    Object.keys(st.pristine).forEach((key) => {
      if (st.pristine[key]) st.v[key] = Dd[key] * k;
    });
  }
  // Rideaux ouverts pendant qu'on mesure la fenêtre, fermés ensuite, sauf choix du client.
  const effView = () => (st.view === "auto" ? (st.step <= 2 ? "open" : "closed") : st.view);

  /* ================= Géométrie de dessin ================= */
  function geom() {
    var Dd = S[st.unit],
      k = kOf(st.unit),
      q = {};
    function pick(key, fb) {
      var v = st.v[key],
        r = RANGES[key];
      if (good(v) && v >= r[0] && v <= r[1]) return v;
      q[key] = true;
      return fb;
    }
    var W = pick("winW", PH.winW),
      H = pick("winH", PH.winH),
      sill = pick("sill", PH.sill);
    var over = pick("overhang", Dd.overhang * k);
    var above = st.mount === "wall" ? pick("above", Dd.above * k) : 0;
    var ceilV =
      good(st.v.ceil) && st.v.ceil >= 0 && st.v.ceil <= 400
        ? st.v.ceil
        : null;
    if (st.mount === "ceiling" && ceilV === null) q.ceil = true;
    var top = sill + H;
    var ceilY =
      ceilV !== null ? top + ceilV : st.mount === "ceiling" ? top + 40 : null;
    var rodY = st.mount === "wall" ? top + above : ceilY;
    var curtTop = st.mount === "wall" ? rodY : ceilY - Dd.ceilClear * k;
    var rodLen = W + 2 * over,
      fullness = st.full === "opt" ? Dd.fullOpt : Dd.fullReco;
    return {
      W: W,
      H: H,
      sill: sill,
      over: over,
      above: above,
      top: top,
      ceilY: ceilY,
      ceilKnown: ceilV !== null,
      rodY: rodY,
      curtTop: curtTop,
      rodLen: rodLen,
      panelW: ((Dd.base === "rod" ? rodLen : W) * fullness) / st.panels,
      brk: st.floor === "break" ? Dd.brk * k : 0,
      q: q,
    };
  }

  /* ================= Schéma SVG ================= */
  function labW(t) {
    return Math.max(18, t.length * 6.3 + 12);
  }
  function lab(x, y, t) {
    var w = labW(t),
      h = 18;
    return (
      '<g class="lab"><rect x="' +
      (x - w / 2) +
      '" y="' +
      (y - h / 2) +
      '" width="' +
      w +
      '" height="' +
      h +
      '" rx="9"/><text class="h6" x="' +
      x +
      '" y="' +
      (y + 0.5) +
      '" text-anchor="middle" dominant-baseline="central">' +
      esc(t) +
      "</text></g>"
    );
  }
  function dimClass(key, unknown, extra) {
    var c = "dim " + (extra || "");
    if (unknown) c += " is-q";
    if (st.focus === key) c += " is-focus";
    else if (REL[st.step].indexOf(key) < 0) c += " is-mute";
    return c;
  }
  function dimV(key, x, yA, yB, t, unknown, extra) {
    var len = yA - yB,
      s = '<g class="' + dimClass(key, unknown, extra) + '">';
    if (len > 1) {
      s +=
        '<line class="halo" x1="' +
        x +
        '" y1="' +
        yA +
        '" x2="' +
        x +
        '" y2="' +
        yB +
        '"/>';
      s +=
        '<line x1="' +
        x +
        '" y1="' +
        yA +
        '" x2="' +
        x +
        '" y2="' +
        yB +
        '"/><line x1="' +
        (x - 5) +
        '" y1="' +
        yA +
        '" x2="' +
        (x + 5) +
        '" y2="' +
        yA +
        '"/><line x1="' +
        (x - 5) +
        '" y1="' +
        yB +
        '" x2="' +
        (x + 5) +
        '" y2="' +
        yB +
        '"/>';
      if (len > 18)
        s +=
          '<path class="ah" d="M' +
          x +
          "," +
          yA +
          'l-3.2,-6.5h6.4z"/><path class="ah" d="M' +
          x +
          "," +
          yB +
          'l-3.2,6.5h6.4z"/>';
    }
    return (
      s + lab(len < 24 ? x - (labW(t) / 2 + 7) : x, (yA + yB) / 2, t) + "</g>"
    );
  }
  function dimH(key, xA, xB, y, t, unknown, extra) {
    var len = xB - xA,
      s = '<g class="' + dimClass(key, unknown, extra) + '">';
    if (len > 1) {
      s +=
        '<line class="halo" x1="' +
        xA +
        '" y1="' +
        y +
        '" x2="' +
        xB +
        '" y2="' +
        y +
        '"/>';
      s +=
        '<line x1="' +
        xA +
        '" y1="' +
        y +
        '" x2="' +
        xB +
        '" y2="' +
        y +
        '"/><line x1="' +
        xA +
        '" y1="' +
        (y - 5) +
        '" x2="' +
        xA +
        '" y2="' +
        (y + 5) +
        '"/><line x1="' +
        xB +
        '" y1="' +
        (y - 5) +
        '" x2="' +
        xB +
        '" y2="' +
        (y + 5) +
        '"/>';
      if (len > 18)
        s +=
          '<path class="ah" d="M' +
          xA +
          "," +
          y +
          'l6.5,-3.2v6.4z"/><path class="ah" d="M' +
          xB +
          "," +
          y +
          'l-6.5,-3.2v6.4z"/>';
    }
    return s + lab((xA + xB) / 2, len < labW(t) + 4 ? y + 15 : y, t) + "</g>";
  }
  /* Cadrage : à chaque étape, le schéma se recadre sur ce qu'elle mesure, et le dessin grandit d'autant.
     Changer d'étape est un mouvement de caméra ; ouvrir ou fermer les rideaux aussi. */
  var shown = { y0: null, y1: null, k: null },
    anim = 0;
  var reduceMotion = !!(
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  function camFor(g) {
    var hi = Math.max(g.rodY, g.ceilY === null ? 0 : g.ceilY);
    if (st.step === 0) return [Math.max(0, g.sill - 0.15 * g.H), g.rodY]; // la fenêtre
    if (st.step === 1) return [0, g.top + 0.08 * g.H]; // du sol au haut de la fenêtre
    if (st.step === 2) return [Math.max(0, g.top - 0.45 * g.H), hi]; // la fenêtre, la tringle, le plafond
    return [0, hi]; // la scène entière
  }
  function targetView(g) {
    var c = camFor(g);
    return { y0: c[0], y1: c[1], k: effView() === "open" ? 1 : 0 };
  }
  function animateView(dur, from) {
    var to = targetView(geom());
    cancelAnimationFrame(anim);
    anim = 0;
    from = from && from.y0 !== null ? from : null;
    if (!from || reduceMotion || !ms.classList.contains("active")) {
      shown = to;
      drawSVG(null, true);
      return;
    }
    var t0 = performance.now();
    var frame = function (now) {
      var t = Math.min(1, (now - t0) / dur),
        e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      shown = {
        y0: from.y0 + (to.y0 - from.y0) * e,
        y1: from.y1 + (to.y1 - from.y1) * e,
        k: from.k + (to.k - from.k) * e,
      };
      drawSVG(null, true);
      anim = t < 1 ? requestAnimationFrame(frame) : 0;
    };
    anim = requestAnimationFrame(frame);
  }
  function snapView() {
    return { y0: shown.y0, y1: shown.y1, k: shown.k };
  }
  /* Matières du schéma : trois petites textures fabriquées une fois au chargement, réutilisées à chaque image.
     Dans le thème, ce seront des images choisies dans l'éditeur, comme les photos du schéma « Comment ça marche ». */
  function makeTextures() {
    if (D.getElementById("kt-tex-wall")) return;
    var rng = function (seed) {
      return function () {
        seed = (seed * 16807) % 2147483647;
        return (seed - 1) / 2147483646;
      };
    };
    var paint = function (w, h, fn) {
      var c = D.createElement("canvas");
      c.width = w;
      c.height = h;
      fn(c.getContext("2d"), w, h);
      return c.toDataURL("image/png");
    };
    var grain = function (x, w, h, amp, r) {
      var img = x.getImageData(0, 0, w, h),
        d = img.data;
      for (var p = 0; p < d.length; p += 4) {
        var n = (r() - 0.5) * amp;
        d[p] += n;
        d[p + 1] += n;
        d[p + 2] += n;
      }
      x.putImageData(img, 0, 0);
    };
    // Mur : enduit clair, nuances douces raccordées sur les bords, grain fin
    var wall = paint(256, 256, function (x, w, h) {
      var r = rng(11);
      x.fillStyle = "#ece7df";
      x.fillRect(0, 0, w, h);
      for (var i = 0; i < 70; i++) {
        var gx = r() * w,
          gy = r() * h,
          rad = 24 + r() * 80,
          a = 0.028 * r(),
          dark = r() < 0.45;
        for (var dx = -w; dx <= w; dx += w)
          for (var dy = -h; dy <= h; dy += h) {
            var gr = x.createRadialGradient(
              gx + dx,
              gy + dy,
              0,
              gx + dx,
              gy + dy,
              rad,
            );
            gr.addColorStop(
              0,
              dark
                ? "rgba(110,95,80," + a + ")"
                : "rgba(255,255,255," + a * 1.6 + ")",
            );
            gr.addColorStop(1, "rgba(0,0,0,0)");
            x.fillStyle = gr;
            x.fillRect(0, 0, w, h);
          }
      }
      grain(x, w, h, 9, r);
    });
    // Sol : parquet de chêne, lames posées dans la longueur
    var floor = paint(512, 64, function (x, w, h) {
      var r = rng(5),
        rows = 4,
        rh = h / rows,
        tones = ["#c8a57a", "#bf9a6c", "#cfae84", "#b8925f", "#c49f73"];
      for (var row = 0; row < rows; row++) {
        var xx = -r() * 160;
        while (xx < w) {
          var len = 120 + r() * 160;
          x.fillStyle = tones[Math.floor(r() * tones.length)];
          x.fillRect(xx, row * rh, len, rh);
          x.fillStyle = "rgba(60,40,20,.35)";
          x.fillRect(xx, row * rh, 1, rh);
          xx += len;
        }
        for (var v = 0; v < 6; v++) {
          var y0 = row * rh + 2 + r() * (rh - 4);
          x.strokeStyle = "rgba(90,60,30," + (0.08 + r() * 0.12) + ")";
          x.lineWidth = 0.6 + r() * 0.8;
          x.beginPath();
          x.moveTo(0, y0);
          for (var px = 0; px <= w; px += 16)
            x.lineTo(px, y0 + Math.sin(px / 40 + v) * 1.2);
          x.stroke();
        }
        x.fillStyle = "rgba(60,40,20,.28)";
        x.fillRect(0, row * rh, w, 1);
      }
      grain(x, w, h, 10, r);
    });
    // Tissu : armure fine, fils plus ou moins sombres ; posée en « produit » sur la couleur du rideau
    var weave = paint(48, 48, function (x, w, h) {
      var r = rng(3);
      x.fillStyle = "#ffffff";
      x.fillRect(0, 0, w, h);
      for (var y = 0; y < h; y += 2) {
        x.fillStyle = "rgba(0,0,0," + (0.02 + r() * 0.03) + ")";
        x.fillRect(0, y, w, 1);
      }
      for (var c = 0; c < w; c += 2) {
        x.fillStyle = "rgba(0,0,0," + (0.015 + r() * 0.025) + ")";
        x.fillRect(c, 0, 1, h);
      }
      grain(x, w, h, 8, r);
    });
    var lib = D.createElementNS("http://www.w3.org/2000/svg", "svg");
    lib.setAttribute("width", "0");
    lib.setAttribute("height", "0");
    lib.setAttribute("aria-hidden", "true");
    lib.style.position = "absolute";
    lib.innerHTML =
      '<defs><pattern id="kt-tex-wall" patternUnits="userSpaceOnUse" width="256" height="256"><image href="' +
      wall +
      '" width="256" height="256"/></pattern>' +
      '<pattern id="kt-tex-floor" patternUnits="userSpaceOnUse" width="512" height="64"><image href="' +
      floor +
      '" width="512" height="64"/></pattern>' +
      '<pattern id="kt-tex-weave" patternUnits="userSpaceOnUse" width="48" height="48"><image href="' +
      weave +
      '" width="48" height="48"/></pattern></defs>';
    D.body.appendChild(lib);
  }
  function drawSVG(r, keep) {
    var svg = $("#kt-ms-svg"),
      Wp = svg.clientWidth,
      Hp = svg.clientHeight;
    if (!Wp || !Hp) return;
    r = r || calc();
    var g = geom(),
      q = g.q;
    if ((!keep && !anim) || shown.y0 === null) shown = targetView(g);
    var k = shown.k;
    svg.setAttribute("viewBox", "0 0 " + Wp + " " + Hp);
    var mL = 44,
      mR = 52,
      mT = 32,
      mB = 14,
      aW = Wp - mL - mR,
      aH = Hp - mT - mB;
    var span = Math.max(20, shown.y1 - shown.y0);
    var sc = Math.min(aW / g.rodLen, aH / span);
    var ox = mL + (aW - g.rodLen * sc) / 2,
      oy = mT + (aH - span * sc) / 2;
    var X = function (x) {
      return ox + (x + g.rodLen / 2) * sc;
    };
    var Y = function (y) {
      return oy + (shown.y1 - y) * sc;
    };
    var fy = Y(0),
      BIG = 4000,
      s = "",
      defs = "",
      f3 = function (v) {
        return v.toFixed(3);
      };
    var unk = {
      rod: q.winW || q.overhang,
      drop: q.winH || q.sill || q.above || q.ceil,
    };
    defs +=
      '<linearGradient id="kt-ms-gf" x1="0" x2="1" y1="0" y2="0"><stop offset="0" stop-color="#8f877c"/><stop offset=".18" stop-color="#cfc8bd"/><stop offset=".45" stop-color="#fbfaf7"/><stop offset=".62" stop-color="#ffffff"/><stop offset=".86" stop-color="#d2ccc1"/><stop offset="1" stop-color="#958d82"/></linearGradient>' +
      '<linearGradient id="kt-ms-sky" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#b9d4e7"/><stop offset=".58" stop-color="#e4eef4"/><stop offset=".6" stop-color="#d6e1d6"/><stop offset="1" stop-color="#c7d6c6"/></linearGradient>' +
      '<radialGradient id="kt-ms-glow"><stop offset="0" stop-color="#fff7e2" stop-opacity=".85"/><stop offset="1" stop-color="#fff7e2" stop-opacity="0"/></radialGradient>' +
      '<linearGradient id="kt-ms-flight" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#fff6df" stop-opacity=".7"/><stop offset="1" stop-color="#fff6df" stop-opacity="0"/></linearGradient>' +
      '<linearGradient id="kt-ms-metal" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#6a615a"/><stop offset=".45" stop-color="#2c2522"/><stop offset="1" stop-color="#120d0b"/></linearGradient>' +
      '<linearGradient id="kt-ms-drop" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#000" stop-opacity=".16"/><stop offset="1" stop-color="#000" stop-opacity="0"/></linearGradient>';

    // Mur, plafond
    s +=
      '<rect x="0" y="0" width="' +
      Wp +
      '" height="' +
      Hp +
      '" fill="url(#kt-tex-wall)"/>';
    var wx0 = X(-g.W / 2),
      wx1 = X(g.W / 2),
      wyT = Y(g.top),
      wyB = Y(g.sill),
      gw = wx1 - wx0,
      gh = wyB - wyT;
    var f = Math.max(2.5, Math.min(8, 5 * sc));
    // Lumière de la fenêtre sur le mur, rideaux ouverts
    s +=
      '<ellipse cx="' +
      (wx0 + wx1) / 2 +
      '" cy="' +
      (wyT + wyB) / 2 +
      '" rx="' +
      gw * 1.15 +
      '" ry="' +
      gh * 0.95 +
      '" fill="url(#kt-ms-glow)" opacity="' +
      f3(0.9 * k) +
      '"/>';
    if (g.ceilY !== null) {
      var cy = Y(g.ceilY);
      s +=
        '<rect x="0" y="' +
        (cy - BIG) +
        '" width="' +
        Wp +
        '" height="' +
        BIG +
        '" fill="#f7f4ef"/><rect x="0" y="' +
        cy +
        '" width="' +
        Wp +
        '" height="10" fill="url(#kt-ms-drop)"/><rect x="0" y="' +
        (cy - 1) +
        '" width="' +
        Wp +
        '" height="1" fill="rgba(0,0,0,.12)"/>';
    }
    // Sol : parquet, ombre au pied du mur, plinthe, tache de lumière sous la fenêtre
    var plH = Math.max(3, 8 * sc);
    s +=
      '<rect x="0" y="' +
      fy +
      '" width="' +
      Wp +
      '" height="' +
      BIG +
      '" fill="url(#kt-tex-floor)"/><rect x="0" y="' +
      fy +
      '" width="' +
      Wp +
      '" height="8" fill="url(#kt-ms-drop)"/>';
    s +=
      '<path d="M' +
      wx0 +
      "," +
      fy +
      " L" +
      wx1 +
      "," +
      fy +
      " L" +
      (wx1 + gw * 0.6) +
      "," +
      (fy + 70) +
      " L" +
      (wx0 - gw * 0.6) +
      "," +
      (fy + 70) +
      'Z" fill="url(#kt-ms-flight)" opacity="' +
      f3(0.85 * k) +
      '"/>';
    s +=
      '<rect x="0" y="' +
      (fy - plH) +
      '" width="' +
      Wp +
      '" height="' +
      plH +
      '" fill="#f8f5f0"/><rect x="0" y="' +
      (fy - plH) +
      '" width="' +
      Wp +
      '" height="1" fill="rgba(0,0,0,.14)"/>';

    // Fenêtre : ombre du dormant, dormant blanc, vitrage (ciel), montant, appui en pierre
    s += '<g class="' + (q.winW || q.winH ? "is-q" : "") + '">';
    s +=
      '<rect x="' +
      (wx0 + 2) +
      '" y="' +
      (wyT + 3) +
      '" width="' +
      gw +
      '" height="' +
      gh +
      '" fill="rgba(40,30,20,.12)"/>';
    s +=
      '<rect class="w-frame" x="' +
      wx0 +
      '" y="' +
      wyT +
      '" width="' +
      gw +
      '" height="' +
      gh +
      '" fill="#fbfaf6" stroke="rgba(0,0,0,.16)" stroke-width="1"/>';
    s +=
      '<rect x="' +
      (wx0 + f) +
      '" y="' +
      (wyT + f) +
      '" width="' +
      Math.max(0, gw - 2 * f) +
      '" height="' +
      Math.max(0, gh - 2 * f) +
      '" fill="url(#kt-ms-sky)"/>';
    s +=
      '<rect x="' +
      (wx0 + f) +
      '" y="' +
      (wyT + f) +
      '" width="' +
      Math.max(0, gw - 2 * f) +
      '" height="' +
      Math.max(1.5, f * 0.8) +
      '" fill="rgba(0,0,0,.08)"/>';
    if (g.W >= 70)
      s +=
        '<rect x="' +
        ((wx0 + wx1) / 2 - f / 2) +
        '" y="' +
        (wyT + f) +
        '" width="' +
        f +
        '" height="' +
        Math.max(0, gh - 2 * f) +
        '" fill="#fbfaf6" stroke="rgba(0,0,0,.12)" stroke-width=".8"/>';
    s +=
      '<g stroke="#ffffff" stroke-opacity=".6" stroke-width="1.6"><line x1="' +
      (wx0 + gw * 0.14) +
      '" y1="' +
      (wyT + gh * 0.42) +
      '" x2="' +
      (wx0 + gw * 0.3) +
      '" y2="' +
      (wyT + gh * 0.2) +
      '"/><line x1="' +
      (wx0 + gw * 0.16) +
      '" y1="' +
      (wyT + gh * 0.55) +
      '" x2="' +
      (wx0 + gw * 0.38) +
      '" y2="' +
      (wyT + gh * 0.24) +
      '"/></g>';
    if (g.sill > 0) {
      var lh = Math.max(3, 4 * sc);
      s +=
        '<rect x="' +
        (wx0 - 6) +
        '" y="' +
        wyB +
        '" width="' +
        (gw + 12) +
        '" height="' +
        lh +
        '" fill="#f3f0e9" stroke="rgba(0,0,0,.12)" stroke-width=".8"/><rect x="' +
        (wx0 - 6) +
        '" y="' +
        (wyB + lh) +
        '" width="' +
        (gw + 12) +
        '" height="7" fill="url(#kt-ms-drop)"/>';
    }
    s += "</g>";
    // Rideaux fermés : la pièce s'assombrit (sous les rideaux, qui restent éclairés côté pièce)
    s +=
      '<rect x="0" y="0" width="' +
      Wp +
      '" height="' +
      Hp +
      '" fill="#1d1712" opacity="' +
      f3(0.18 * (1 - k)) +
      '"/>';

    // Tringle ou rail : ombre, supports, barre métal, embouts
    var rx0 = X(-g.rodLen / 2),
      rx1 = X(g.rodLen / 2),
      ry = Y(g.rodY),
      rh = Math.max(3.5, 2.5 * sc);
    if (st.mount === "wall") {
      s +=
        '<rect x="' +
        (rx0 - 3) +
        '" y="' +
        (ry + rh + 2) +
        '" width="' +
        (rx1 - rx0 + 6) +
        '" height="3" fill="rgba(0,0,0,.10)"/>';
      [rx0 + 10, rx1 - 10].forEach(function (bx) {
        s +=
          '<rect x="' +
          (bx - 1.5) +
          '" y="' +
          (ry - 3) +
          '" width="3" height="' +
          (rh + 6) +
          '" fill="#2c2522"/>';
      });
      s +=
        '<rect x="' +
        (rx0 - 3) +
        '" y="' +
        ry +
        '" width="' +
        (rx1 - rx0 + 6) +
        '" height="' +
        rh +
        '" rx="' +
        rh / 2 +
        '" fill="url(#kt-ms-metal)"/>';
      s +=
        '<circle cx="' +
        (rx0 - 5) +
        '" cy="' +
        (ry + rh / 2) +
        '" r="' +
        (rh * 0.6 + 2) +
        '" fill="url(#kt-ms-metal)"/><circle cx="' +
        (rx1 + 5) +
        '" cy="' +
        (ry + rh / 2) +
        '" r="' +
        (rh * 0.6 + 2) +
        '" fill="url(#kt-ms-metal)"/>';
    } else {
      s +=
        '<rect x="' +
        rx0 +
        '" y="' +
        ry +
        '" width="' +
        (rx1 - rx0) +
        '" height="' +
        Math.max(4, rh) +
        '" fill="url(#kt-ms-metal)"/>';
    }

    // Rideaux : k = 1 ouverts (rangés sur les côtés), k = 0 fermés
    var n = st.panels,
      pwCm = g.rodLen / n,
      topPx = Y(g.curtTop),
      bot = g.brk > 0 ? fy + Math.max(2.5, g.brk * sc) : fy - 1.5;
    var folds = Math.max(3, Math.min(24, Math.round(g.panelW / 20)));
    var sx = Math.min(1, Math.max(g.over + 8, pwCm * 0.2) / pwCm),
      scaleX = 1 + (sx - 1) * k,
      curtains = "",
      labels = "";
    for (var i = 0; i < n; i++) {
      var x0 = X(-g.rodLen / 2 + i * pwCm),
        x1 = X(-g.rodLen / 2 + (i + 1) * pwCm);
      if (n === 2) {
        if (i === 0) x1 -= 0.75;
        else x0 += 0.75;
      }
      // Bords des plis : largeurs inégales (0,8 à 1,25), toujours les mêmes pour un même rideau
      var wts = [],
        tot = 0;
      for (var j = 0; j < folds; j++) {
        var wt = 0.8 + 0.45 * (0.5 + 0.5 * Math.sin(j * 2.39 + i * 1.7));
        wts.push(wt);
        tot += wt;
      }
      var edges = [x0],
        acc = x0;
      for (j = 0; j < folds; j++) {
        acc += ((x1 - x0) * wts[j]) / tot;
        edges.push(acc);
      }
      var sw = (x1 - x0) / folds,
        d = "M" + x0 + "," + topPx + "H" + x1 + "V" + bot;
      for (j = folds - 1; j >= 0; j--) {
        var fw = edges[j + 1] - edges[j];
        d +=
          "Q" +
          (edges[j] + fw / 2) +
          "," +
          (bot + Math.min(4, fw * 0.18)) +
          " " +
          edges[j] +
          "," +
          bot;
      }
      d += "Z";
      var shade = "";
      for (j = 0; j < folds; j++)
        shade +=
          '<rect x="' +
          edges[j] +
          '" y="' +
          (topPx - 2) +
          '" width="' +
          (edges[j + 1] - edges[j] + 0.4) +
          '" height="' +
          (bot - topPx + 8) +
          '" fill="url(#kt-ms-gf)"/>';
      defs +=
        '<clipPath id="kt-ms-cp' + i + '"><path d="' + d + '"/></clipPath>';
      var o = n === 2 ? (i === 0 ? x0 : x1) : st.side === "left" ? x0 : x1;
      curtains +=
        '<g class="cur" transform="translate(' +
        o +
        " 0) scale(" +
        scaleX.toFixed(4) +
        " 1) translate(" +
        -o +
        ' 0)">' +
        '<path d="' +
        d +
        '" fill="rgba(40,28,18,.14)" transform="translate(4 3)"/>' +
        '<path d="' +
        d +
        '" fill="' +
        esc(st.curtain) +
        '" stroke="rgba(0,0,0,.18)" stroke-width=".8"/>' +
        '<g clip-path="url(#kt-ms-cp' +
        i +
        ')" style="mix-blend-mode:multiply">' +
        shade +
        "</g>" +
        '<path d="' +
        d +
        '" fill="url(#kt-tex-weave)" style="mix-blend-mode:multiply" opacity=".9"/>' +
        '<rect x="' +
        x0 +
        '" y="' +
        topPx +
        '" width="' +
        (x1 - x0) +
        '" height="' +
        Math.max(3, 8 * sc) +
        '" fill="rgba(0,0,0,.06)"/>' +
        "</g>";
      var txt = r.ok
        ? fmtU(r.orderW, st.unit) + " × " + fmtU(r.orderH, st.unit)
        : "?";
      if (r.ok && labW(txt) + 2 > x1 - x0) txt = fmtU(r.orderW, st.unit);
      var plY = Math.min(Hp - 30, Math.max(mT + 20, Y(g.curtTop * 0.5)));
      labels +=
        '<g class="pl' +
        (REL[st.step].indexOf("panel") < 0 ? " is-mute" : "") +
        '"><g opacity="' +
        f3(1 - k) +
        '">' +
        lab((x0 + x1) / 2, plY, txt) +
        "</g></g>";
    }
    s +=
      curtains +
      labels +
      '<rect class="ghost" opacity="' +
      f3(0.8 * (1 - k)) +
      '" x="' +
      wx0 +
      '" y="' +
      wyT +
      '" width="' +
      gw +
      '" height="' +
      gh +
      '"/>';

    // Cotes : celles de la fenêtre visibles rideaux ouverts, effacées rideaux fermés
    var qm = function (key, val) {
      return q[key] ? "?" : L(val);
    };
    s += '<g opacity="' + f3(k) + '">';
    s += dimH(
      "winW",
      wx0 + f + 2,
      wx1 - f - 2,
      Y(g.sill + g.H * 0.2),
      qm("winW", g.W),
      q.winW,
      "in-win",
    );
    s += dimV(
      "winH",
      X(g.W * 0.25),
      wyB - f - 2,
      wyT + f + 2,
      qm("winH", g.H),
      q.winH,
      "in-win",
    );
    if (g.sill > 0 || q.sill)
      s += dimV(
        "sill",
        X(-g.W * 0.25),
        fy,
        wyB,
        qm("sill", g.sill),
        q.sill,
        "in-win",
      );
    if (st.mount === "wall" && (g.above > 0 || q.above))
      s += dimV(
        "above",
        X(-g.W * 0.25),
        wyT,
        ry,
        qm("above", g.above),
        q.above,
        "in-win",
      );
    if (st.mount === "ceiling")
      s += dimV(
        "ceil",
        X(-g.W * 0.25),
        wyT,
        Y(g.ceilY),
        q.ceil ? "?" : L(g.ceilY - g.top),
        q.ceil,
        "in-win",
      );
    s += "</g>";
    if (st.mount !== "ceiling" && g.ceilKnown)
      s += dimV(
        "ceil",
        rx0 - 24,
        wyT,
        Y(g.ceilY),
        L(g.ceilY - g.top),
        false,
        "",
      );
    if (g.over > 0 || q.overhang)
      s += dimH(
        "overhang",
        wx1,
        rx1,
        ry + 13,
        qm("overhang", g.over),
        q.overhang,
        "",
      );
    s += dimH(
      "rod",
      rx0,
      rx1,
      ry - 14,
      (st.mount === "wall" ? T.rod : T.rail) + " " +
        (unk.rod ? "?" : L(g.rodLen)),
      unk.rod,
      "dim--out",
    );
    s += dimV(
      "drop",
      rx1 + 24,
      g.brk > 0 ? fy + g.brk * sc : fy,
      topPx,
      r.ok ? fmtU(r.orderH, st.unit) : "?",
      unk.drop,
      "dim--out",
    );
    svg.innerHTML =
      '<title id="kt-ms-svg-t">' +
      esc(T.svg_title) +
      '</title><desc id="kt-ms-svg-d">' +
      esc($("#kt-ms-cap").textContent) +
      "</desc><defs>" +
      defs +
      "</defs>" +
      s;
  }

  /* ================= Rendu du tiroir ================= */
  function errText(e) {
    if (e === "empty") return T.err_empty;
    if (e === "nan") return st.unit === "in" ? T.err_nan_in : T.err_nan_cm;
    if (e === "ceiling") return T.err_ceiling;
    if (e === "clear") return T.err_clear;
    if (e && e.min !== undefined) return tr("err_range", { min: L(e.min, 0), max: U(e.max, 0) });
    return "";
  }
  function syncInputs(force) {
    FIELDS.forEach((key) => {
      const el = $("#kt-ms-f-" + key);
      if (!force && D.activeElement === el) return;
      const v = st.v[key];
      if (Number.isNaN(v)) return;
      el.value = v === null || v === undefined ? "" : L(v);
    });
    $$(".input-unit").forEach((e) => (e.textContent = UL()));
  }
  function syncRadios() {
    ["mount", "panels", "floor"].forEach((name) => {
      $$('input[data-ms-radio="' + name + '"]').forEach((i) => (i.checked = String(st[name]) === i.value));
    });
  }
  function renderFields(r) {
    FIELDS.forEach((key) => {
      const e = r.err[key],
        show = !!(e && st.shown[key]),
        p = $("#kt-ms-e-" + key);
      $('[data-box="' + key + '"]').classList.toggle("kt-ms__box--err", show);
      p.hidden = !show;
      p.textContent = show ? errText(e) : "";
      $("#kt-ms-f-" + key).setAttribute("aria-invalid", String(show));
    });
    const Dd = S[st.unit];
    $('[data-field="above"]').hidden = st.mount !== "wall";
    // Au mur : hauteur au-dessus de la fenêtre, puis débord, puis plafond (facultatif).
    // Au plafond : distance au plafond, puis débord. La distance verticale vient toujours en premier.
    const fOver = $('[data-field="overhang"]'),
      fCeil = $('[data-field="ceil"]');
    if (st.mount === "ceiling") {
      if (fCeil.nextElementSibling !== fOver) fOver.parentNode.insertBefore(fCeil, fOver);
    } else if (fOver.nextElementSibling !== fCeil) fOver.parentNode.insertBefore(fCeil, fOver.nextElementSibling);
    $("#kt-ms-l-ceil").textContent = st.mount === "wall" ? T.ceil_optional : T.ceil;
    $("#kt-ms-h-ceil").textContent =
      st.mount === "wall" ? T.hint_ceil_wall : tr("hint_ceil_ceiling", { clear: withUnit(fmtU(Dd.ceilClear, st.unit)) });
    $("#kt-ms-h-above").textContent = tr("hint_above", { above: withUnit(fmtU(Dd.above, st.unit)) });
    $("#kt-ms-h-overhang").textContent = tr("hint_overhang", { overhang: withUnit(fmtU(Dd.overhang, st.unit)) });
    ["reco", "opt"].forEach((id) => {
      $('[data-full-title="' + id + '"]').textContent = "×" + fmtNum(id === "opt" ? Dd.fullOpt : Dd.fullReco);
    });
    $("#kt-ms-h-full").textContent =
      tr("hint_fullness", {
        x: fmtNum(Dd.fullReco),
        base: Dd.base === "rod" ? T.base_rod : T.base_window,
      }) + (PERF === "insulating" || PERF === "thermal" ? " " + T.hint_fullness_insulating : "");
    $$("[data-full]").forEach((c) => {
      const on = c.getAttribute("data-full") === st.full;
      c.setAttribute("data-on", String(on));
      const h = $(".kt-size-card__head", c);
      h.setAttribute("aria-checked", String(on));
      h.tabIndex = on ? 0 : -1;
    });
    $("#kt-ms-floor-break-l").textContent = tr("floor_break", { n: withUnit(fmtU(Dd.brk, st.unit)) });
  }
  function renderCaption(r) {
    const cap = $("#kt-ms-cap");
    if (r.ok)
      cap.textContent = tr(r.qty > 1 ? "caption_other" : "caption_one", {
        n: r.qty,
        size: withUnit(fmtU(r.orderW, st.unit) + " x " + fmtU(r.orderH, st.unit)),
      });
    else {
      const first = FIELDS.filter((k) => r.err[k])[0];
      cap.textContent = first ? tr("caption_missing", { field: T["name_" + first] }) : "";
    }
    $("#kt-ms-view").checked = effView() === "closed";
  }
  // La fixation cochée sur la fiche, recopiée avec son rendu « choisi » (image encadrée, nom dessous). La copie perd
  // nom, formulaire, valeur et data-* : elle n'entre ni dans le formulaire ni dans le groupe de boutons de la fiche.
  function fixationPick() {
    const input = $('#kt-ms-fixes input[type="radio"]:checked') || pdpFixation();
    if (!input) return "";
    const item = input.closest(".fixation-item").cloneNode(true);
    const radio = item.querySelector('input[type="radio"]');
    [...radio.attributes].forEach((a) => {
      if (a.name !== "type" && a.name !== "class") radio.removeAttribute(a.name);
    });
    radio.setAttribute("checked", "");
    item.querySelectorAll("[for], [id]").forEach((el) => {
      el.removeAttribute("for");
      el.removeAttribute("id");
    });
    const name = (item.querySelector(".fixation-text") || item).textContent.trim();
    return (
      '<span class="fixation-swatch-container kt-ms__fix" inert aria-hidden="true">' +
      item.outerHTML +
      '</span><span class="visually-hidden">' +
      esc(name) +
      "</span>"
    );
  }
  const qtyOf = (r) => st.qty || r.qty || 1;
  // Cote en cm telle que la lit le bloc des tailles (au mm).
  const cm1 = (x) => Math.round(x * 10) / 10;
  // Une taille de la grille de la fiche ?
  const isStandard = (r) =>
    [...D.querySelectorAll(".std-size-btn[data-w][data-h]")].some(
      (b) => !ms.contains(b) && Math.abs(+b.dataset.w - r.orderWcm) < 0.6 && Math.abs(+b.dataset.h - r.orderHcm) < 0.6
    );
  // Étape 5 : la taille en tête (taille standard ou sur mesure, quantité), puis la couleur et la fixation dans le
  // rendu « choisi » de la fiche.
  function renderResult(r) {
    const u = st.unit;
    $("#kt-ms-missing").hidden = r.ok;
    $("#kt-ms-size").textContent = r.ok ? withUnit(fmtU(r.orderW, u) + " x " + fmtU(r.orderH, u)) : "";
    const qty = qtyOf(r);
    $("#kt-ms-size-sub").textContent = r.ok
      ? (isStandard(r) ? T.standard : T.custom) + " · " + qty + " " + (qty > 1 ? T.panel_other : T.panel_one)
      : "";
    $("#kt-ms-qty").value = String(qty);
    $("#kt-ms-qty-value").textContent = qty;
    // Le texte du bouton d'achat de la fiche, prix compris, calculé par le bloc des tailles pour cette taille.
    const quote =
      r.ok && window.SurMesureData && window.SurMesureData.quote
        ? window.SurMesureData.quote(st.fixation, cm1(r.orderWcm), cm1(r.orderHcm), qty)
        : null;
    $("#kt-ms-add").innerHTML = quote || "<span>" + esc(T.add_to_cart) + "</span>";
    // La couleur choisie, dessinée comme la pastille active de la fiche (.cs-sibling-item.active-swatch).
    $("#kt-ms-color").innerHTML = st.color.name
      ? '<span class="kt-ms__swatch cs-sibling-item active-swatch" aria-hidden="true"><a tabindex="-1"><span class="cs-sibling-color-dot" style="' +
        esc(st.color.style) +
        '"></span></a></span><span class="kt-recap__text">' +
        esc(st.color.name) +
        "</span>"
      : "";
    const fix = fixationPick();
    $("#kt-ms-fix").innerHTML = fix;
    $("#kt-ms-fix-row").hidden = !fix;
  }
  const stepValid = (i, r) => STEP_KEYS[i].every((key) => !r.err[key]);
  function renderSteps(r) {
    let reach = true;
    $$(".kt-ms__bar button").forEach((b, i) => {
      b.disabled = !reach && i !== st.step;
      b.classList.toggle("is-done", i < st.step);
      if (i === st.step) b.setAttribute("aria-current", "step");
      else b.removeAttribute("aria-current");
      if (!stepValid(i, r)) reach = false;
    });
    $("#kt-ms-stepnow").textContent = tr("step_now", { n: st.step + 1, total: 5 });
    $$(".kt-ms__step").forEach((p) => (p.hidden = +p.getAttribute("data-panel") !== st.step));
    const last = st.step === 4,
      next = $("#kt-ms-next");
    $("#kt-ms-prev").hidden = st.step === 0;
    next.hidden = last;
    next.firstElementChild.textContent = st.step === 3 ? T.see_size : T.next;
    $("#kt-ms-buy").hidden = !last;
    $("#kt-ms-add").disabled = !r.ok;
    $("#kt-ms-apply").hidden = !last;
    $("#kt-ms-apply").disabled = !r.ok;
  }
  function update() {
    const r = calc();
    renderFields(r);
    renderCaption(r);
    renderResult(r);
    renderSteps(r);
    drawSVG(r);
  }

  /* ================= Ouverture et fermeture : eventModal du thème ================= */
  const isOpen = () => ms.classList.contains("active");
  function openMs(step) {
    if (step !== undefined) st.step = step;
    const u = pdpUnit();
    if (u && u !== st.unit) setUnit(u, true);
    syncFixation();
    // Option mobile du thème : sous 1025 px, la feuille du bas se ferme en tirant l'en-tête (draggable-modal).
    if (!isOpen()) eventModal(ms, "open", false, null, true);
    update();
    requestAnimationFrame(update);
  }
  const closeMs = () => {
    if (isOpen()) eventModal(ms, "close");
  };
  // Fermé par la croix, le voile, Échap ou la poignée : on range le clavier.
  const onModalClosed = () =>
    setTimeout(() => {
      if (isOpen()) return;
      if (D.activeElement && ms.contains(D.activeElement) && D.activeElement.blur) D.activeElement.blur();
      setKb(0, null);
    }, 0);
  D.addEventListener("modal:closed", onModalClosed);
  // Seul le corps du tiroir défile : si un élément ciblé fait défiler le tiroir lui-même, on le remet en place.
  const inner = $(".kt-ms__inner");
  inner.addEventListener("scroll", () => {
    if (inner.scrollTop) inner.scrollTop = 0;
  });

  /* Clavier du téléphone : lu dans visualViewport. Ouvert, le schéma se réduit, « Suivant » reste au-dessus. */
  const isText = (el) => el && el.tagName === "INPUT" && el.type !== "radio" && el.type !== "checkbox";
  function keepVisible(el) {
    setTimeout(() => {
      const body = $("#kt-ms-body"),
        box = el.closest(".custom-input-group") || el;
      body.scrollTop += box.getBoundingClientRect().top - body.getBoundingClientRect().top - 12;
    }, 60);
  }
  function setKb(px, el) {
    ms.style.setProperty("--kb", px + "px");
    const on = !!el && ms.contains(el) && px > 0;
    ms.classList.toggle("kb-open", on);
    if (on) keepVisible(el);
  }
  const nativeKb = () => {
    if (window.innerWidth > 1024) return;
    const el = D.activeElement,
      on = isText(el) && ms.contains(el),
      vv = window.visualViewport;
    let kb = 0;
    if (on && vv) kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    setKb(kb > 80 ? kb : 0, on ? el : null);
  };
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", nativeKb);
    window.visualViewport.addEventListener("scroll", nativeKb);
  }
  ms.addEventListener("focusin", () => setTimeout(nativeKb, 0));
  ms.addEventListener("focusout", () => setTimeout(nativeKb, 0));

  /* ================= Navigation et saisie ================= */
  function goStep(i) {
    const from = snapView();
    st.step = i;
    update();
    animateView(560, from);
    $("#kt-ms-body").scrollTop = 0;
    const h = $('.kt-ms__step[data-panel="' + i + '"] .kt-ms__q');
    if (h && isOpen()) h.focus({ preventScroll: true });
  }
  function next() {
    if (st.step === 4) return;
    const r = calc(),
      bad = STEP_KEYS[st.step].filter((key) => r.err[key] && !$('[data-box="' + key + '"]').closest("[hidden]"));
    if (bad.length) {
      bad.forEach((key) => (st.shown[key] = true));
      update();
      $("#kt-ms-f-" + bad[0]).focus();
      return;
    }
    goStep(st.step + 1);
  }
  // La taille va au bloc des tailles, qui l'écrit et répond dans detail : applied, standard (taille de la grille).
  function sendToPdp(r) {
    const detail = {
      unit: st.unit,
      width: r.orderW,
      height: r.orderH,
      widthCm: Math.round(r.orderWcm * 100) / 100,
      heightCm: Math.round(r.orderHcm * 100) / 100,
      qty: qtyOf(r),
      // Longueur de tringle (fenêtre + débord des deux côtés), lue par l'encart accessoire de la fiche.
      rodCm: Math.round(r.rodLen * 10) / 10,
      mount: st.mount,
    };
    D.dispatchEvent(new CustomEvent("kt:measure:apply", { detail: detail }));
    return detail;
  }
  // Après fermeture du tiroir : la page descend au bloc des tailles, la notification du thème confirme (en haut à
  // droite sur ordinateur, en bas sur téléphone).
  function showOnPdp(r) {
    const target = pdpContainer();
    const message = tr("applied", {
      size: withUnit(fmtU(r.orderW, st.unit) + " x " + fmtU(r.orderH, st.unit)),
      qty: qtyOf(r) + " " + (qtyOf(r) > 1 ? T.panel_other : T.panel_one),
    });
    setTimeout(() => {
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }
      notifier.show(esc(message), "success", 5000);
      const toast = D.querySelector("#notification-container .notification:last-child");
      if (toast) toast.classList.add("kt-ms-toast");
    }, 350);
  }
  // Un autre coloris : sa fiche s'ouvre. La configuration la suit (sessionStorage) et s'y applique au chargement,
  // avant l'ajout au panier si c'était l'action demandée (resume, au démarrage).
  const PENDING = "kt-measure-pending";
  const pdpFixation = () =>
    [...D.querySelectorAll('input[data-option-position="3"]')].find((i) => i.checked && !ms.contains(i));
  function goToColor(action) {
    const pending = {
      path: new URL(st.color.url, window.location.href).pathname,
      at: Date.now(),
      action: action,
      fixation: st.fixation,
      state: {
        unit: st.unit,
        v: st.v,
        pristine: st.pristine,
        mount: st.mount,
        panels: st.panels,
        full: st.full,
        floor: st.floor,
        qty: st.qty,
      },
    };
    try {
      sessionStorage.setItem(PENDING, JSON.stringify(pending));
    } catch (e) {
      /* sans stockage, la fiche du coloris s'ouvre sans la taille */
    }
    window.location.href = st.color.url;
  }
  // La fixation choisie passe sur la fiche (son libellé, comme un clic du client) ; le thème met l'offre à jour,
  // la taille vient ensuite.
  function withPdpFixation(value, fn) {
    const input =
      value &&
      [...D.querySelectorAll('input[data-option-position="3"]')].find((i) => i.value === value && !ms.contains(i));
    if (!input || input.checked) return fn();
    (D.querySelector('label[for="' + input.id + '"]') || input).click();
    setTimeout(fn, 900);
  }
  function apply() {
    const r = calc();
    if (!r.ok) return;
    if (st.color.url) return goToColor("apply");
    closeMs();
    withPdpFixation(st.fixation, () => {
      const detail = sendToPdp(r);
      if (detail.applied) showOnPdp(r);
    });
  }
  // Ajouter au panier : la taille va dans la fiche, puis le bouton d'achat de la fiche fait le reste (propriétés du
  // sur-mesure, rideau assemblé, ouverture du panier). Il est cliqué une fois le tiroir fermé : eventModal retire
  // open-modal 300 ms après une fermeture, ce qui débloquerait la page sous le panier ouvert entre-temps.
  const buyButton = () => D.querySelector('form[action*="/cart/add"] button[name="add"], .product-form__submit');
  function addToCart() {
    const r = calc();
    if (!r.ok) return;
    if (st.color.url) return goToColor("add");
    closeMs();
    withPdpFixation(st.fixation, () => {
      const detail = sendToPdp(r);
      const buy = buyButton();
      if (!detail.applied || !buy || buy.disabled) {
        if (detail.applied) showOnPdp(r);
        return;
      }
      setTimeout(() => buy.click(), 400);
    });
  }
  $$(".kt-ms__bar button").forEach((b) => b.addEventListener("click", () => goStep(+b.getAttribute("data-step"))));
  $("#kt-ms-next").addEventListener("click", next);
  $("#kt-ms-prev").addEventListener("click", () => {
    if (st.step > 0) goStep(st.step - 1);
  });
  $("#kt-ms-add").addEventListener("click", addToCart);
  $("#kt-ms-apply").addEventListener("click", apply);

  /* ================= Fixation : le sélecteur de la fiche, recopié ================= */
  // Copie de la grille de la fiche (même rendu) dans un groupe de boutons à part : sans nom, formulaire, data-* ni
  // identifiant de la fiche, elle ne touche ni au formulaire ni à l'option ; « Ajouter » ou « Appliquer » la reporte.
  const fixes = $("#kt-ms-fixes"),
    fixEdit = $("#kt-ms-fix-edit");
  const pdpFixGrid = [...D.querySelectorAll(".fixation-swatch-container .fixation-grid-wrapper")].find((el) => !ms.contains(el));
  if (pdpFixGrid) {
    const grid = pdpFixGrid.cloneNode(true);
    grid.querySelectorAll('input[type="radio"]').forEach((input, n) => {
      const label = grid.querySelector('label[for="' + input.id + '"]'),
        id = "kt-ms-fx-" + n;
      [...input.attributes].forEach((a) => {
        if (!["type", "class", "value"].includes(a.name)) input.removeAttribute(a.name);
      });
      input.id = id;
      input.name = "kt-ms-fixation";
      if (label) label.setAttribute("for", id);
    });
    grid.querySelectorAll("[id]:not(input)").forEach((el) => el.removeAttribute("id"));
    const wrap = D.createElement("div");
    wrap.className = "fixation-swatch-container";
    wrap.append(grid);
    fixes.append(wrap);
  }
  fixEdit.hidden = $$('input[type="radio"]', fixes).length < 2;
  fixEdit.addEventListener("click", () => {
    const show = fixes.hidden;
    fixes.hidden = !show;
    fixEdit.setAttribute("aria-expanded", String(show));
  });
  fixes.addEventListener("change", (e) => {
    if (e.target.name !== "kt-ms-fixation") return;
    st.fixation = e.target.value;
    update();
  });
  // À l'ouverture : la fixation cochée sur la fiche.
  function syncFixation() {
    const input = pdpFixation();
    st.fixation = input ? input.value : null;
    $$('input[type="radio"]', fixes).forEach((i) => (i.checked = i.value === st.fixation));
  }

  /* ================= Couleur : le sélecteur de la fiche, sur place ================= */
  const colors = $("#kt-ms-colors"),
    colorEdit = $("#kt-ms-color-edit");
  colorEdit.hidden = $$(".cs-sibling-item a[href]", colors).length < 2;
  colorEdit.addEventListener("click", () => {
    const show = colors.hidden;
    colors.hidden = !show;
    colorEdit.setAttribute("aria-expanded", String(show));
    // La rangée qui défile recalcule ses flèches une fois visible.
    if (show) window.dispatchEvent(new Event("resize"));
  });
  // Une pastille change la couleur du schéma et du récapitulatif, sans quitter la page.
  colors.addEventListener("click", (e) => {
    const a = e.target.closest(".cs-sibling-item a[href]");
    if (!a) return;
    e.preventDefault();
    const href = a.getAttribute("href"),
      url = new URL(href, window.location.href),
      dot = a.querySelector(".cs-sibling-color-dot"),
      here = url.pathname === window.location.pathname;
    // Seulement une fiche du même site : l'adresse sert ensuite à changer de page.
    if (url.origin !== window.location.origin) return;
    $$(".cs-sibling-item", colors).forEach((li) => li.classList.toggle("active-swatch", li === a.closest(".cs-sibling-item")));
    st.color = {
      name: a.getAttribute("data-color-label") || a.title,
      style: dot ? dot.getAttribute("style") : "",
      url: here ? null : url.pathname + url.search,
    };
    st.curtain = safeColor(cfg.siblings && cfg.siblings[href]) || safeColor(dot && dot.style.backgroundColor) || CURTAIN;
    renderResult(calc());
    drawSVG();
  });
  $("#kt-ms-view").addEventListener("change", (e) => {
    const from = snapView();
    st.view = e.target.checked ? "closed" : "open";
    update();
    animateView(480, from);
  });
  FIELDS.forEach((key) => {
    const el = $("#kt-ms-f-" + key);
    el.addEventListener("input", () => {
      st.v[key] = parseLen(el.value, st.unit);
      if (key in st.pristine) st.pristine[key] = false;
      update();
    });
    el.addEventListener("focus", () => {
      st.focus = key;
      st.view = "auto";
      drawSVG();
    });
    el.addEventListener("blur", () => {
      st.focus = null;
      st.shown[key] = true;
      syncInputs();
      update();
    });
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        el.blur();
        next();
      }
    });
  });
  ["mount", "panels", "floor"].forEach((name) => {
    $$('input[data-ms-radio="' + name + '"]').forEach((i) => {
      i.addEventListener("change", () => {
        st[name] = name === "panels" ? +i.value : i.value;
        // Un ou deux rideaux : la quantité suit.
        if (name === "panels") st.qty = null;
        update();
      });
    });
  });
  $("#kt-ms-qty").addEventListener("change", (e) => {
    st.qty = +e.target.value || null;
    update();
  });
  $$("[data-full]").forEach((c) => {
    const h = $(".kt-size-card__head", c),
      pickIt = () => {
        st.full = c.getAttribute("data-full");
        update();
      };
    c.addEventListener("click", pickIt);
    h.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        pickIt();
      }
    });
  });

  /* ================= Unité : celle du bloc des tailles ================= */
  function setUnit(u, fromPdp) {
    u = u === "in" ? "in" : "cm";
    if (u !== st.unit) {
      st.unit = u;
      applyDefaults();
      syncInputs(true);
      update();
    }
    $$("[data-unit-select]").forEach((s) => (s.value = u));
    if (fromPdp) return;
    const sel = D.getElementById("KtUnitSelect");
    if (sel && sel.value !== u) {
      sel.value = u;
      sel.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  $$("[data-unit-select]").forEach((s) => s.addEventListener("change", () => setUnit(s.value, false)));
  const onPdpUnit = (e) => {
    if (e.target && e.target.id === "KtUnitSelect") setUnit(e.target.value, true);
  };
  D.addEventListener("change", onPdpUnit);

  /* ======= Ouverture : bouton « Aide à la mesure » (data-kt-measure-open), ou tout lien vers #<ancre> ======= */
  const onClick = (e) => {
    const link = e.target.closest && e.target.closest("[data-kt-measure-open], a[href*='#']");
    if (!link || ms.contains(link)) return;
    if (!link.hasAttribute("data-kt-measure-open")) {
      const url = new URL(link.getAttribute("href"), window.location.href);
      if (url.pathname !== window.location.pathname || decodeURIComponent(url.hash.slice(1)) !== ANCHOR) return;
    }
    e.preventDefault();
    openMs();
  };
  D.addEventListener("click", onClick);
  // Éditeur de thème : sélectionner la section ouvre le tiroir.
  const onSectionSelect = (e) => {
    if (e.detail && e.detail.sectionId === ms.dataset.sectionId) openMs();
  };
  const onSectionDeselect = (e) => {
    if (e.detail && e.detail.sectionId === ms.dataset.sectionId) closeMs();
  };
  D.addEventListener("shopify:section:select", onSectionSelect);
  D.addEventListener("shopify:section:deselect", onSectionDeselect);

  /* ================= Bouton « Ajouter au panier » : les couleurs du bouton d'achat de la fiche ================= */
  (() => {
    const buy = buyButton();
    if (!buy) return;
    const cs = getComputedStyle(buy);
    [
      "--btn-primary-bg-color",
      "--btn-primary-bg-color-rgb",
      "--btn-primary-color",
      "--btn-primary-hover-bg-color",
      "--btn-primary-hover-color",
    ].forEach((name) => {
      const value = cs.getPropertyValue(name).trim();
      if (value) $("#kt-ms-buy").style.setProperty(name, value);
    });
  })();

  /* ================= Configuration venue d'un autre coloris (goToColor) ================= */
  // Le bloc des tailles annonce qu'il est prêt (variantes chargées) par kt:sur-mesure:ready.
  function whenPdpReady(fn) {
    if (window.SurMesureData && window.SurMesureData.ready) fn();
    else D.addEventListener("kt:sur-mesure:ready", fn, { once: true });
  }
  // Le thème ne charge ses scripts d'achat et de variantes qu'au premier geste du visiteur (theme.js) : sur la fiche
  // qu'on vient d'ouvrir, personne n'a encore touché la page, et le bouton d'achat enverrait le formulaire vers /cart
  // au lieu d'ouvrir le panier. On les charge comme le ferait ce premier geste (5 s au plus).
  const loadBuyScripts = () =>
    Promise.race([
      Promise.all([import("@NextSkyTheme/add-to-cart"), import("@NextSkyTheme/variant-swatch")]).then(() =>
        customElements.whenDefined("product-form")
      ),
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]).catch(() => {});
  function resume(pending) {
    loadBuyScripts().then(() => resumeNow(pending));
  }
  function resumeNow(pending) {
    const run = () => {
      const r = calc();
      if (!r.ok) return;
      const detail = sendToPdp(r);
      if (!detail.applied) return;
      const buy = buyButton();
      if (pending.action === "add" && buy && !buy.disabled) setTimeout(() => buy.click(), 300);
      else showOnPdp(r);
    };
    withPdpFixation(pending.fixation, run);
  }
  let pending = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING) || "null");
  } catch (e) {
    pending = null;
  }
  if (pending && pending.path === window.location.pathname && Date.now() - pending.at < 10 * 60 * 1000) {
    try {
      sessionStorage.removeItem(PENDING);
    } catch (e) {
      /* rien */
    }
  } else pending = null;

  /* ================= Démarrage ================= */
  const ro = window.ResizeObserver
    ? new ResizeObserver(() => {
        if (isOpen()) drawSVG();
      })
    : null;
  if (ro) ro.observe($("#kt-ms-svg"));
  makeTextures();
  applyDefaults();
  syncFixation();
  if (pending) {
    Object.assign(st, pending.state, { step: 4, shown: {} });
    if (pending.fixation) {
      st.fixation = pending.fixation;
      $$('input[type="radio"]', fixes).forEach((i) => (i.checked = i.value === st.fixation));
    }
  }
  $$("[data-unit-select]").forEach((s) => (s.value = st.unit));
  syncRadios();
  syncInputs(true);
  update();
  if (pending) whenPdpReady(() => resume(pending));
  else if (decodeURIComponent(window.location.hash.slice(1)) === ANCHOR) requestAnimationFrame(() => openMs(0));

  return () => {
    D.removeEventListener("modal:closed", onModalClosed);
    D.removeEventListener("change", onPdpUnit);
    D.removeEventListener("click", onClick);
    D.removeEventListener("shopify:section:select", onSectionSelect);
    D.removeEventListener("shopify:section:deselect", onSectionDeselect);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", nativeKb);
      window.visualViewport.removeEventListener("scroll", nativeKb);
    }
    if (ro) ro.disconnect();
  };
}

class KtMeasureDrawer extends HTMLElement {
  connectedCallback() {
    if (this.cleanup) return;
    this.cleanup = init(this);
  }

  disconnectedCallback() {
    if (this.cleanup) this.cleanup();
    this.cleanup = null;
  }
}

if (!customElements.get("kt-measure-drawer")) customElements.define("kt-measure-drawer", KtMeasureDrawer);
